import type { LatestDemandSummary } from './useLatestDemandsReceived';
import moment from '~~/utils/moment';
import { UnpaidLevel, isUnpaidLevel } from '~/app/auth/enums/UnpaidLevel';
import SubscriptionInvoice from '~/app/subscription/models/SubscriptionInvoice';
import type { TenantPaymentDetails } from '~/app/auth/endpoints/TenantEndpoint';

/**
 * Local test forcing for the unpaid / non-renewing experience, in the same
 * spirit as the `freeplan` flag (see useUpsellAccess): lets anyone preview the
 * banners, the blocked state and the invoices screen without real Chargebee
 * data. Reload the app after changing a flag.
 *
 *   localStorage.setItem('unpaid-level', '2')     // force unpaid level (0 to 4)
 *   localStorage.setItem('non-renewing', 'true')  // force the non-renewing banner
 *   localStorage.removeItem('unpaid-level')       // back to real data
 */
export const getForcedUnpaidLevel = (): UnpaidLevel | null => {
    try {
        const raw = localStorage.getItem('unpaid-level');
        if (raw === null) return null;
        const parsed = Number(raw);

        return isUnpaidLevel(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

export const isNonRenewingForced = (): boolean => {
    try {
        return localStorage.getItem('non-renewing') === 'true';
    } catch {
        return false;
    }
};

const DEMO_INVOICE_AMOUNT = 14900;

/**
 * Demo invoices for the settings invoices screen when the subscription API has
 * no data for the local tenant: as many unpaid invoices as the forced status
 * announces, plus a small paid history.
 */
export const makeUnpaidDemoInvoices = (unpaidCount: number): SubscriptionInvoice[] => {
    const unpaid = Array.from({ length: unpaidCount }, (_, index) => {
        return new SubscriptionInvoice({
            id: `DEMO-${2051 + index}`,
            date: moment()
                .subtract(index + 1, 'month')
                .unix(),
            status: index === 0 ? 'payment_due' : 'not_paid',
            total: DEMO_INVOICE_AMOUNT,
            amount_paid: 0,
            amount_due: DEMO_INVOICE_AMOUNT,
        });
    });

    const paid = Array.from({ length: 2 }, (_, index) => {
        return new SubscriptionInvoice({
            id: `DEMO-${2049 - index}`,
            date: moment()
                .subtract(unpaidCount + index + 1, 'month')
                .unix(),
            status: 'paid',
            total: DEMO_INVOICE_AMOUNT,
            amount_paid: DEMO_INVOICE_AMOUNT,
            amount_due: 0,
        });
    });

    return [...unpaid, ...paid];
};

// Placeholder shown in place of the backend-generated EPC QR code when previewing
// with demo data: a plain grey patterned square, obviously not scannable.
const DEMO_QR_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 8 8"><rect width="8" height="8" fill="#F3F3F3"/><path fill="#9CA3AF" d="M0 0h3v3H0zM5 0h3v3H5zM0 5h3v3H0z"/><path fill="#fff" d="M1 1h1v1H1zM6 1h1v1H6zM1 6h1v1H1z"/><rect x="5" y="5" width="1" height="1" fill="#9CA3AF"/><rect x="7" y="6" width="1" height="1" fill="#9CA3AF"/><rect x="6" y="7" width="1" height="1" fill="#9CA3AF"/></svg>`;

/**
 * Demo payment details for the transfer modal when the real endpoint is not
 * reachable in forced mode (TDD-5631 branch not running locally). Mirrors the
 * backend payload: amount in euros, communication = tenant uuid + invoice id.
 */
export const makeUnpaidDemoPaymentDetails = (tenantId: string, invoices: SubscriptionInvoice[]): TenantPaymentDetails => {
    const amountInCents = invoices.reduce((sum, invoice) => sum + invoice.totalDue, 0);
    const invoiceId = invoices.length === 1 ? invoices[0].id : null;

    return {
        beneficiary: 'Trustup SRL (démo)',
        iban: 'BE00 0000 0000 0000',
        bic: 'DEMOBEBB',
        amount: amountInCents / 100,
        communication: [tenantId, invoiceId].filter(Boolean).join(' '),
        qrCode: `data:image/svg+xml;base64,${btoa(DEMO_QR_SVG)}`,
    };
};

/**
 * Demo demands for the level 4 "Dernières demandes reçues" panel when the
 * backend endpoint has no local data (TDD-5604).
 */
export const makeUnpaidDemoDemands = (): LatestDemandSummary[] => {
    const demands: Array<Pick<LatestDemandSummary, 'title' | 'excerpt' | 'status'>> = [
        { title: 'Rénovation salle de bain', excerpt: 'Bonjour, je souhaite rénover entièrement ma salle de bain de 8 m²...', status: 'pending' },
        { title: 'Placement terrasse', excerpt: 'Nous cherchons un professionnel pour une terrasse en bois de 25 m²...', status: 'pending' },
        { title: 'Peinture intérieure', excerpt: 'Peinture de 3 chambres et un séjour, murs et plafonds...', status: 'interested' },
        { title: 'Isolation toiture', excerpt: "Demande de devis pour l'isolation d'une toiture inclinée...", status: 'pending' },
        { title: 'Carrelage cuisine', excerpt: 'Pose de carrelage au sol dans une cuisine de 15 m²...', status: 'quote_sent' },
    ];

    return demands.map((demand, index) => ({
        id: `DEMO-DEMAND-${index + 1}`,
        date: moment().subtract(index + 1, 'day'),
        ...demand,
    }));
};
